import { useMemo } from 'react';

import { isEqual, uniqWith } from 'lodash/fp';

import { GENDER_ID_KEY, MEASURE_ID_KEY, YEAR_KEY } from '@portal/common/models/data-key';
import { localizeCondition } from '@portal/common/utility/chart-data-helpers';

import config from '../../../config';
import { NO_BOUND_VALUE } from './CustomAgeGroupSelector';
import { AGE_GROUP_SEPARATOR } from './SavedAgeGroups';

const FATAL_MEASURES_IDS = [config.deathMeasureId, config.yllMeasureId];
const CDN_NOT_MODELED_AGE_GROUP_IDS_KEYS = [
    'fatal_female_age_group_restriction_ids',
    'fatal_male_age_group_restriction_ids',
    'non_fatal_female_age_group_restriction_ids',
    'non_fatal_male_age_group_restriction_ids',
];

const toNumericAgeRange = (range: (number | string)[]): [number, number] => {
    const min = Number(range[0]);
    const maxRaw = range[1];
    const max = maxRaw === NO_BOUND_VALUE ? Infinity : Number(maxRaw);
    return [min, max];
};

export const useConditionsHaveNotModeledAgeGroups = ({
    conditionDetailNotes,
    selectedFilters,
    currentAgeGroup,
    savedAgeGroups,
    isSingleAgesReport,
    ageGroupEntities,
}) =>
    useMemo(() => {
        if (!conditionDetailNotes) return [];

        const measures = selectedFilters[MEASURE_ID_KEY] || [];
        const genders = selectedFilters[GENDER_ID_KEY] || [];
        const isFatalIncluded = measures.some((m) => FATAL_MEASURES_IDS.includes(m));
        const isNonFatalIncluded = measures.some((m) => !FATAL_MEASURES_IDS.includes(m));
        const hasBothGenders = genders.includes(config.bothGendersId);
        const hasMaleGender = genders.includes(config.maleGenderId) || hasBothGenders;
        const hasFemaleGender = genders.includes(config.femaleGenderId) || hasBothGenders;

        const filteredAgeGroupKeys = CDN_NOT_MODELED_AGE_GROUP_IDS_KEYS.filter((key) => {
            if (key.startsWith('fatal_') && !isFatalIncluded) return false;
            if (key.startsWith('non_fatal_') && !isNonFatalIncluded) return false;
            if (key.includes('female') && !hasFemaleGender) return false;
            if (key.includes('male') && !hasMaleGender) return false;
            return true;
        });
        const currentAgeGroupMapped = toNumericAgeRange(currentAgeGroup);

        const ageGroupsToQuery = isSingleAgesReport
            ? [currentAgeGroupMapped]
            : uniqWith(isEqual, [
                  currentAgeGroupMapped,
                  ...savedAgeGroups.map((group) =>
                      toNumericAgeRange(group.split(AGE_GROUP_SEPARATOR))
                  ),
              ]);
        const conditions: string[] = [];

        conditionDetailNotes.forEach((item) => {
            const restrictedAgeIds = new Set<number>();

            filteredAgeGroupKeys.forEach((key) => {
                const ids = item.records?.find((r) => r.key === key)?.value;
                if (Array.isArray(ids)) {
                    ids.forEach((id) => restrictedAgeIds.add(id));
                }
            });

            if (!restrictedAgeIds.size) return;

            const restrictedAgeGroups = [...restrictedAgeIds]
                .map((id) => ageGroupEntities.find((g) => g.id === id))
                .filter(
                    (group) =>
                        group &&
                        group.min_age_time_unit === YEAR_KEY &&
                        group.max_age_time_unit === YEAR_KEY
                );
            const hasRestrictedAgeOverlap = ageGroupsToQuery.some(([min, max]) => {
                return restrictedAgeGroups.some(
                    ({ min_age, max_age }) => min <= max_age && max >= min_age
                );
            });

            if (hasRestrictedAgeOverlap) {
                conditions.push(
                    localizeCondition(
                        Object.fromEntries(item.metaRecords.map(({ key, value }) => [key, value]))
                    )
                );
            }
        });

        return conditions;
    }, [
        conditionDetailNotes,
        selectedFilters,
        currentAgeGroup,
        savedAgeGroups,
        isSingleAgesReport,
        ageGroupEntities,
    ]);

export default useConditionsHaveNotModeledAgeGroups;
